/**
 * Everything the console reads, and the calls it makes.
 *
 * The reads are live. `onSnapshot` rather than a fetch, because two people
 * work these screens at once: an accountant verifying a payment while the
 * committee watches the same month should not have to be told to refresh, and
 * a resident whose payment was just checked should see it change.
 *
 * Nothing here computes money. `@liyu/core` turns documents into figures, and
 * it is the same code the rollup function runs, which is the only reason the
 * browser and the server can be trusted to agree. Anything this file worked
 * out for itself would be a second opinion about a balance.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import type { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  type ComplaintDoc,
  type ComplaintStatus,
  type Mover,
  type Announcement,
  type Entitlements,
  type TelebirrOrder,
  type InboxItem,
  toInboxItem,
  type ChapaOrder,
  type ChapaReceipt,
  toChapaReceipt,
  advertisementInput,
  type ContractorTrade,
  TRADE_NAMES,
  tradeOf,
  announcementInput,
  entitlementsOf,
  offersTelebirr,
  offersChapa,
  toAnnouncement,
  toTelebirrOrder,
  toChapaOrder,
  chapaOrdersToRecheck,
  nextEntry,
  orgCollection,
  receiptPath,
  toComplaint,
  type HeldDoc,
  toHeld,
  toLedgerEntry,
  toPayment,
  toRollup,
  toUnit,
  CHAT_ROOM,
  CHAT_WINDOW,
  type ChatMember,
  type ChatMessage,
  type ChatOffSpan,
  type ChatRole,
  type ChatSettings,
  chatCooldownInput,
  chatMessageInput,
  chatNameInput,
  chatOffUntil,
  inOrder,
  toChatMember,
  toChatMessage,
  toChatSettings,
} from "@liyu/core";
import type { CallableName, InputOf, OutputOf } from "@liyu/core";
import type { ComplaintCategory, LedgerEntry, Period, PeriodSummary, UnitRow } from "@liyu/core";
import { db, functions, storage } from "./firebase";

export interface Unit extends UnitRow {
  monthlyDue: number;
}

export interface Payment {
  id: string;
  unitId: string;
  amount: number;
  status: "submitted" | "verified" | "rejected";
  method: string;
  monthsAhead: number;
  customAmount?: boolean;
  reference: string | null;
  receiptPath: string | null;
  campaignId: string | null;
  submittedAt: Date | null;
  /** Why the accountant turned it down or took it back, word for word. */
  reason: string | null;
}

export interface Expense {
  id: string;
  amount: number;
  /** The month it was spent in, which is not always the month it was recorded. */
  period: Period;
  category: string;
  /** Who was paid. */
  vendor: string;
  description: string;
  at: Date | null;
  voided: boolean;
}

/** Parsed by core, the same way the phone parses it; `at` is when it was reported. */
export type Complaint = ComplaintDoc & { at: Date | null };

/** A notice, read the same way the phone reads it. */
export type Notice = Announcement;

export interface Campaign {
  id: string;
  title: string;
  target: number;
  raised: number;
  status: "open" | "closed";
}

export interface Change {
  id: string;
  action: string;
  at: Date | null;
  by: string | null;
  reason: string | null;
}

export interface Held {
  id: string;
  unitId: string;
  amount: number;
}

export interface Reminder {
  id: string;
  amount: number;
  months: number;
  createdAt: Date | null;
}

/** Resolve a receipt path through Firebase Storage so staff access is checked by rules. */
export function receiptUrl(path: string): Promise<string> {
  return getDownloadURL(ref(storage, path));
}

/**
 * What Liyu has told this person, newest first. Filtered to their own rows,
 * which is also all the rules let them read; sorted here, because a month of
 * one person's notifications is a handful and an index for it is not worth a
 * deploy.
 */
export function inbox(
  orgId: string,
  uid: string,
  on: (rows: InboxItem[]) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    query(collection(db, orgCollection(orgId, "notifications")), where("recipientUid", "==", uid)),
    (snapshot) => {
      on(
        snapshot.docs
          .flatMap((row) => {
            // Opening one stamps it with the server's clock, which this side
            // only learns a moment later; until then the time is estimated,
            // so the bell drops the moment it is opened rather than never.
            const item = toInboxItem(row.id, row.data({ serverTimestamps: "estimate" }));
            return item ? [item] : [];
          })
          .sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)),
      );
    },
    bad,
  );
}

/** Opening a notification marks it read; the time is the server's. */
export async function markRead(orgId: string, id: string): Promise<void> {
  await updateDoc(doc(db, orgCollection(orgId, "notifications"), id), {
    readAt: serverTimestamp(),
  });
}

export function reminders(
  orgId: string,
  unitId: string,
  on: (rows: Reminder[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "reminders"),
    (row): Reminder => ({
      id: row.id,
      amount: whole(row.get("amount")),
      months: whole(row.get("months")),
      createdAt: at(row.get("createdAt")),
    }),
    on,
    bad,
    where("unitId", "==", unitId),
  );
}

/** A Firestore timestamp, or nothing, without pretending a missing one is now. */
function at(value: unknown): Date | null {
  const stamp = value as { toDate?: () => Date } | undefined;
  return typeof stamp?.toDate === "function" ? stamp.toDate() : null;
}

function whole(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) ? value : 0;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

type Row = QueryDocumentSnapshot<DocumentData>;

export type Stop = () => void;

/**
 * One live collection, handed to a callback whenever it changes.
 *
 * A refusal is passed on rather than logged and swallowed. A screen that
 * silently shows nothing because the rules turned the read down is a screen
 * that says the building has no units.
 */
function live<T>(
  path: string,
  parse: (row: Row) => T,
  onRows: (rows: T[]) => void,
  onProblem: (cause: Error) => void,
  ...constraints: Parameters<typeof query> extends [unknown, ...infer C] ? C : never[]
): Stop {
  return onSnapshot(
    query(collection(db, path), ...constraints),
    (snapshot) => {
      onRows(snapshot.docs.map(parse));
    },
    onProblem,
  );
}

export function units(orgId: string, on: (rows: Unit[]) => void, bad: (e: Error) => void): Stop {
  return live(
    orgCollection(orgId, "units"),
    (row): Unit => {
      // The same parser the phone uses, so the two read a unit the same way.
      const unit = toUnit(row.data());
      return {
        unitId: row.id,
        block: unit.block,
        termId: unit.currentTermId,
        active: unit.active,
        billingPaused: unit.billingPaused,
        monthlyDue: unit.monthlyDue,
      };
    },
    on,
    bad,
  );
}

/**
 * One unit's rollups, for the resident who lives in it.
 *
 * Scoped on the unit and the occupancy because the rules are: a resident may
 * read a rollup row only where both match their own token, and a query that
 * does not say so is refused as a whole rather than filtered. The console read
 * the collection unscoped for everybody, so every screen a resident opened was
 * an error about a missing property.
 */
export function summariesFor(
  orgId: string,
  unitId: string,
  termId: string,
  on: (rows: { unitId: string; summary: PeriodSummary }[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "unitPeriods"),
    rollup,
    on,
    bad,
    where("unitId", "==", unitId),
    where("termId", "==", termId),
  );
}

/**
 * A rollup, parsed by core rather than here.
 *
 * The hand-written version read `balance`, and the document's field is
 * `closingBalance`, so every figure the console drew from it was nought: what
 * a unit owed, what the building was owed, and what the resident's own screen
 * told them they had left to pay. `toRollup` is the parser the functions and
 * the phone already share, and it also refuses a row that is missing a field
 * rather than quietly reading it as zero.
 */
function rollup(row: Row): { unitId: string; summary: PeriodSummary } {
  return toRollup(row.data());
}

/**
 * The rollups staff screens need, and no more: every month from `since` for
 * the trend and the current map, and any older month still owed, for arrears.
 * Reading every rollup the complex ever had grew by a month per unit per month.
 */
export function recentSummaries(
  orgId: string,
  since: Period,
  on: (rows: { unitId: string; summary: PeriodSummary }[]) => void,
  bad: (e: Error) => void,
): Stop {
  const path = orgCollection(orgId, "unitPeriods");
  type Rows = Map<string, { unitId: string; summary: PeriodSummary }>;
  // Both halves must have answered once before the screen gets either.
  const halves: { recent: Rows | null; owing: Rows | null } = { recent: null, owing: null };
  const send = () => {
    if (halves.recent && halves.owing) {
      on([...new Map([...halves.owing, ...halves.recent]).values()]);
    }
  };
  const follow = (half: keyof typeof halves, constraint: ReturnType<typeof where>) =>
    onSnapshot(
      query(collection(db, path), constraint),
      (snapshot) => {
        halves[half] = new Map(snapshot.docs.map((row) => [row.id, rollup(row)] as const));
        send();
      },
      bad,
    );
  const stops = [
    follow("recent", where("period", ">=", since)),
    follow("owing", where("closingBalance", ">", 0)),
  ];
  return () => stops.forEach((stop) => stop());
}

/** Core's payment parser, shared with the phone, plus what only this app shows. */
function payment(row: Row): Payment {
  const doc = toPayment(row.data());
  return {
    id: row.id,
    unitId: doc.unitId,
    amount: doc.amount,
    status: doc.status,
    method: doc.method,
    monthsAhead: doc.monthsAhead,
    customAmount: row.get("customAmount") === true,
    reference: doc.reference,
    receiptPath: doc.receiptPath,
    campaignId: doc.campaignId,
    submittedAt: at(row.get("submittedAt")),
    reason:
      text(row.get("rejectionReason")).trim() || text(row.get("reversalReason")).trim() || null,
  };
}

export function waiting(
  orgId: string,
  on: (rows: Payment[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "payments"),
    payment,
    on,
    bad,
    where("status", "==", "submitted"),
  );
}

/**
 * One unit's payment history, for its details panel. Opened when the panel is,
 * rather than reading every payment the complex has ever had.
 */
export function unitPayments(
  orgId: string,
  unitId: string,
  on: (rows: Payment[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(orgCollection(orgId, "payments"), payment, on, bad, where("unitId", "==", unitId));
}

export function paymentsFor(
  orgId: string,
  unitId: string,
  termId: string,
  on: (rows: Payment[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "payments"),
    payment,
    on,
    bad,
    where("unitId", "==", unitId),
    where("termId", "==", termId),
  );
}

/**
 * A resident's own entries, which name the months each payment settled and
 * price paying ahead with the same engine the verification runs.
 */
export function ledgerFor(
  orgId: string,
  unitId: string,
  termId: string,
  on: (rows: LedgerEntry[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "ledger"),
    (row) => toLedgerEntry(row.id, row.data()),
    on,
    bad,
    where("unitId", "==", unitId),
    where("termId", "==", termId),
  );
}

/** What a resident's payments brought beyond what was owed, resolved or not. */
export function extrasFor(
  orgId: string,
  unitId: string,
  termId: string,
  on: (rows: HeldDoc[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "heldFunds"),
    (row) => toHeld(row.data()),
    on,
    bad,
    where("unitId", "==", unitId),
    where("termId", "==", termId),
  );
}

/** The resident's own unit's monthly fee, which the rules let them read. */
export function monthlyDueOf(
  orgId: string,
  unitId: string,
  on: (monthlyDue: number) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    doc(db, orgCollection(orgId, "units"), unitId),
    (snapshot) => {
      const found = snapshot.data();
      on(found ? toUnit(found).monthlyDue : 0);
    },
    bad,
  );
}

export function expenses(
  orgId: string,
  since: Period,
  on: (rows: Expense[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "expenses"),
    (row): Expense => ({
      id: row.id,
      amount: whole(row.get("amount")),
      period: text(row.get("period")) as Period,
      category: text(row.get("category")),
      vendor: text(row.get("vendor")),
      description: text(row.get("note")),
      at: at(row.get("spentAt")) ?? at(row.get("recordedAt")),
      voided: row.get("voided") === true,
    }),
    on,
    bad,
    where("period", ">=", since),
  );
}

export function complaints(
  orgId: string,
  on: (rows: Complaint[]) => void,
  bad: (e: Error) => void,
  /** A resident may read only their own, so theirs is the query they get. */
  ...mine: ReturnType<typeof where>[]
): Stop {
  return live(
    orgCollection(orgId, "complaints"),
    (row): Complaint => {
      const complaint = toComplaint(row.id, row.data());
      return { ...complaint, at: complaint.reportedAt };
    },
    on,
    bad,
    ...mine,
  );
}

export interface Advertisement {
  id: string;
  businessName: string;
  service: string;
  description: string;
  phone: string;
  /** From the fixed list; a listing saved before trades is placed by its words. */
  trade: ContractorTrade;
}

/**
 * The committee's contractor listings, checked the way the phone checks them.
 * A listing that fails the check is left out rather than taking the list down.
 */
export function advertisements(
  orgId: string,
  on: (rows: Advertisement[]) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    collection(db, orgCollection(orgId, "advertisements")),
    (snapshot) => {
      on(
        snapshot.docs.flatMap((row) => {
          try {
            const service = text(row.get("service"));
            return [
              {
                id: row.id,
                ...advertisementInput({
                  businessName: text(row.get("businessName")),
                  service,
                  description: text(row.get("description")),
                  phone: text(row.get("phone")),
                }),
                trade: tradeOf({ trade: row.get("trade") as unknown, service }),
              },
            ];
          } catch {
            return [];
          }
        }),
      );
    },
    bad,
  );
}

/**
 * The committee adding or editing a listing, in the shape the rules check:
 * the four fields core validates, who changed it and when, and on a new one
 * the complex and who created it.
 */
export async function saveAdvertisement(
  orgId: string,
  uid: string,
  input: { businessName: string; trade: ContractorTrade; description: string; phone: string },
  id?: string,
): Promise<void> {
  // The trade's English name goes into `service` too, which is what a phone
  // that predates trades shows.
  const values = {
    ...advertisementInput({ ...input, service: TRADE_NAMES[input.trade] }),
    updatedBy: uid,
    updatedAt: serverTimestamp(),
  };
  if (id) {
    await updateDoc(doc(db, orgCollection(orgId, "advertisements"), id), values);
    return;
  }
  await addDoc(collection(db, orgCollection(orgId, "advertisements")), {
    ...values,
    orgId,
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
}

export async function removeAdvertisement(orgId: string, id: string): Promise<void> {
  await deleteDoc(doc(db, orgCollection(orgId, "advertisements"), id));
}

export function notices(
  orgId: string,
  on: (rows: Notice[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "announcements"),
    (row) => toAnnouncement(row.id, row.data({ serverTimestamps: "estimate" })),
    on,
    bad,
  );
}

export function campaigns(
  orgId: string,
  on: (rows: Campaign[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "campaigns"),
    (row): Campaign => ({
      id: row.id,
      title: text(row.get("title")),
      target: whole(row.get("target")),
      raised: whole(row.get("raised")),
      status: text(row.get("status")) === "closed" ? "closed" : "open",
    }),
    on,
    bad,
  );
}

/**
 * Payments Chapa confirmed, newest first: the accountant's Chapa receipts and
 * the committee's Chapa money. Written only by the function that asked Chapa.
 */
export function chapaReceipts(
  orgId: string,
  on: (rows: ChapaReceipt[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "payments"),
    (row) => toChapaReceipt(row.id, row.data()),
    (rows) =>
      on(
        rows
          .filter((row): row is ChapaReceipt => row !== null)
          .sort((a, b) => (b.paidAt?.getTime() ?? 0) - (a.paidAt?.getTime() ?? 0)),
      ),
    bad,
    where("method", "==", "chapa"),
  );
}

export function held(orgId: string, on: (rows: Held[]) => void, bad: (e: Error) => void): Stop {
  return live(
    orgCollection(orgId, "heldFunds"),
    (row): Held => ({
      id: row.id,
      unitId: text(row.get("unitId")),
      amount: whole(row.get("amount")),
    }),
    on,
    bad,
    where("status", "==", "unresolved"),
  );
}

export function changes(
  orgId: string,
  on: (rows: Change[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "changeLog"),
    (row): Change => ({
      id: row.id,
      action: text(row.get("action")),
      at: at(row.get("at")),
      by: (row.get("actor") as string | null) ?? null,
      reason: (row.get("reason") as string | null) ?? null,
    }),
    on,
    bad,
    orderBy("at", "desc"),
    // The journal screen shows what happened lately; the admin console's
    // journal is the place to read a whole month.
    limit(200),
  );
}

/**
 * Who the accounts in this organization belong to, by uid.
 *
 * The change log signs every row with a uid, because a uid is the only thing
 * about an account that cannot be edited or reused. That makes it the right
 * thing to store and the wrong thing to show: a journal whose author column is
 * twenty-eight characters of base64 is a journal nobody reads. This turns it
 * back into the door number or the staff id.
 *
 * Staff only. The rules let an account read the users of its own organization
 * and refuse a resident, which is why this is not opened for one.
 */
/** An account, as the accountant's unit page needs it. */
export interface Login {
  uid: string;
  appRole: string;
  unitId: string | null;
  termId: string | null;
  active: boolean;
  setUp: boolean;
  phone: string | null;
  name: string | null;
}

/** Who each journal row names, and every account in the complex. */
export function people(
  orgId: string,
  on: (rows: Map<string, string>, logins: Login[]) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    query(collection(db, "users"), where("orgId", "==", orgId)),
    (snapshot) => {
      on(
        new Map(
          snapshot.docs.map((row) => [
            row.id,
            text(row.get("unitId")) === "" ? text(row.get("loginId")) : text(row.get("unitId")),
          ]),
        ),
        snapshot.docs.map((row) => ({
          uid: row.id,
          appRole: text(row.get("appRole")),
          unitId: text(row.get("unitId")) || null,
          termId: text(row.get("termId")) || null,
          active: row.get("active") !== false,
          setUp: row.get("mustChangePassword") !== true,
          phone: text(row.get("phone")) || null,
          name: text(row.get("name")) || null,
        })),
      );
    },
    bad,
  );
}

/** pending, active or suspended. Members can read it whatever it is. */
export async function organizationStatus(orgId: string): Promise<string> {
  return text((await getDoc(doc(db, "organizations", orgId))).get("status"));
}

/** The complex's name, the extras its plan includes, and whether Telebirr and Chapa are on. */
export function organization(
  orgId: string,
  on: (name: string, entitlements: Entitlements, telebirr: boolean, chapa: boolean) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    doc(db, "organizations", orgId),
    (snapshot) => {
      on(
        text(snapshot.get("name")),
        entitlementsOf(snapshot.data()),
        offersTelebirr(snapshot.data()),
        offersChapa(snapshot.data()),
      );
    },
    bad,
  );
}

/**
 * One month of the ledger, read once rather than watched.
 *
 * Every other read here is a live listener, because the screens redraw when
 * the building changes. This one answers a question asked at a moment: what
 * did this month look like when I pressed the button. A listener would be a
 * subscription nobody unsubscribes and a statement that changed while it was
 * being read.
 *
 * Filtered on the period alone and sorted here. Ordering in the query as well
 * would need a composite index, and a month of a building is a few hundred
 * rows, so the sort costs nothing and the deploy does.
 */
export async function monthLedger(orgId: string, period: Period): Promise<LedgerEntry[]> {
  const found = await getDocs(
    query(collection(db, orgCollection(orgId, "ledger")), where("period", "==", period)),
  );
  return found.docs
    .map((row) => toLedgerEntry(row.id, row.data()))
    .sort((a, b) => a.date.localeCompare(b.date) || a.unitId.localeCompare(b.unitId));
}

/* ------------------------------------------------------------- calls ---- */

/**
 * The writes, every one of them a callable.
 *
 * Nothing here writes to Firestore directly, and the rules would refuse if it
 * tried. A verification moves money between a payment, a ledger and a rollup
 * in one transaction and writes the row saying who did it; a browser that
 * managed half of that would leave the books saying something untrue.
 */
/**
 * The org is added here rather than at each call site.
 *
 * Every one of these guards on `orgId` and refuses without it, and the org a
 * caller may act in is the one on their token, so there is nothing for a view
 * to decide. Leaving it to the caller is how all six of these shipped without
 * it and failed on `invalid-argument` the first time anybody pressed them.
 */
function call<N extends CallableName>(name: N) {
  return async (orgId: string, args: Omit<InputOf<N>, "orgId">): Promise<OutputOf<N>> =>
    (
      await httpsCallable<InputOf<N>, OutputOf<N>>(
        functions,
        name,
      )({ ...args, orgId } as InputOf<N>)
    ).data;
}

export const verifyPayment = call("verifyPaymentCall");
export const rejectPayment = call("rejectPaymentCall");
export const recordExpense = call("recordExpenseCall");
export const remindUnits = call("remindUnitsCall");
export const createCampaign = call("createCampaignCall");
export const closeCampaign = call("closeCampaignCall");
export const recordPayment = call("recordPaymentCall");
export const reversePayment = call("reversePaymentCall");
export const postAdjustment = call("postAdjustmentCall");
export const voidInvoice = call("voidInvoiceCall");
export const changeUnit = call("changeUnitCall");
export const changeBuilding = call("changeBuildingCall");
export const resolveHeldFunds = call("resolveHeldFundsCall");
export const voidExpense = call("voidExpenseCall");
export const setAccountBalance = call("setAccountBalanceCall");
export const resetPassword = call("resetPasswordCall");
export const handOverUnit = call("handOverUnitCall");
export const setAccountActive = call("setAccountActiveCall");
export const startTelebirrPayment = call("startTelebirrPaymentCall");
export const checkTelebirrPayment = call("checkTelebirrPaymentCall");

/** One Telebirr order, as the function moves it from started to paid. */
export function telebirrOrder(
  orderId: string,
  on: (order: TelebirrOrder | null) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    doc(db, "telebirrOrders", orderId),
    (snapshot) => on(snapshot.exists() ? toTelebirrOrder(snapshot.id, snapshot.data()) : null),
    bad,
  );
}

export const startChapaPayment = call("startChapaPaymentCall");
export const checkChapaPayment = call("checkChapaPaymentCall");
/** Asks Chapa about one payment for its receipt. Reads only. */
export const chapaReceipt = call("chapaReceiptCall");

/** The resident's own Chapa orders that may still have a payment behind them. */
export async function unfinishedChapaOrders(
  orgId: string,
  unitId: string,
  termId: string,
): Promise<string[]> {
  const snapshot = await getDocs(
    query(
      collection(db, "chapaOrders"),
      where("orgId", "==", orgId),
      where("unitId", "==", unitId),
      where("termId", "==", termId),
      where("status", "==", "started"),
    ),
  );
  return chapaOrdersToRecheck(
    snapshot.docs.map((row) => toChapaOrder(row.id, row.data())),
    new Date(),
  );
}

/** One Chapa order, as the function moves it from started to paid. */
export function chapaOrder(
  orderId: string,
  on: (order: ChapaOrder | null) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    doc(db, "chapaOrders", orderId),
    (snapshot) => on(snapshot.exists() ? toChapaOrder(snapshot.id, snapshot.data()) : null),
    bad,
  );
}

export interface Account {
  id: string;
  name: string;
  balance: number;
  countedAt: Date | null;
}

/** What the building holds, as the accountant last counted each account. */
export function accounts(
  orgId: string,
  on: (rows: Account[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "accounts"),
    (row): Account => ({
      id: row.id,
      name: text(row.get("name")) || row.id,
      balance: whole(row.get("balance")),
      countedAt: at(row.get("reconciledAt")),
    }),
    on,
    bad,
  );
}

export interface Building {
  id: string;
  defaultMonthlyDue: number;
  dueDayOfMonth: number;
  autoReminders: boolean;
}

/** The building's defaults, for the accountant's settings. */
export function building(
  orgId: string,
  buildingId: string,
  on: (row: Building | null) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    doc(db, orgCollection(orgId, "buildings"), buildingId),
    (snapshot) =>
      on(
        snapshot.exists()
          ? {
              id: snapshot.id,
              defaultMonthlyDue: whole(snapshot.get("defaultMonthlyDue")),
              dueDayOfMonth: whole(snapshot.get("dueDayOfMonth")),
              autoReminders: snapshot.get("autoReminders") === true,
            }
          : null,
      ),
    bad,
  );
}

/**
 * A resident submitting their own payment.
 *
 * This is the one write in the app that does not go through a callable, and
 * the rules are what make that safe: a resident may create a payment only
 * against the unit and occupancy in their own token, only with their own uid
 * on it, and only with the status `submitted`. They cannot verify it, and
 * nothing about a balance moves until an accountant does.
 *
 * The termId is the reason a new occupant cannot pay into the last one's
 * ledger: it is reminted when a unit changes hands and the rules compare it.
 */
export async function submitPayment(
  orgId: string,
  fields: {
    unitId: string;
    termId: string;
    buildingId: string;
    amount: number;
    monthsAhead: number;
    customAmount?: boolean;
    reference: string | null;
    campaignId: string | null;
    submittedBy: string;
    /** From `uploadReceipt`, under the resident's own unit and occupancy. */
    receiptPath: string | null;
  },
): Promise<void> {
  if (!Number.isInteger(fields.amount) || fields.amount <= 0) {
    throw new Error(`a payment is whole birr above zero, received ${String(fields.amount)}`);
  }
  await addDoc(collection(db, orgCollection(orgId, "payments")), {
    ...fields,
    status: "submitted",
    method: "bank_transfer",
    submittedAt: serverTimestamp(),
  });
}

const RECEIPT_TYPES: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
};

/**
 * The photograph of a transfer, uploaded before the payment that points at it,
 * the same order the phone uses: a payment only exists once its evidence does.
 * Storage rules take one image, under ten megabytes, written once.
 */
export async function uploadReceipt(
  orgId: string,
  unitId: string,
  termId: string,
  file: File,
): Promise<string> {
  const extension = RECEIPT_TYPES[file.type];
  if (!extension) {
    throw new Error(
      `a receipt is a photograph (JPEG, PNG, WebP or HEIC), not ${file.type || "this file"}`,
    );
  }
  const path = receiptPath({
    orgId,
    unitId,
    termId,
    receiptId: `${String(Date.now())}-${Math.random().toString(36).slice(2, 8)}`,
    extension,
  });
  await uploadBytes(ref(storage, path), file, { contentType: file.type });
  return path;
}

/**
 * Moving a repair on, checked by core's state machine before it is written,
 * the same way the phone moves it. The rules check the same moves again.
 */
export async function moveComplaint(
  orgId: string,
  complaint: Complaint,
  to: ComplaintStatus,
  mover: Mover,
  by: string,
  extra: { note?: string; assignee?: { name: string; phone: string; trade: string } } = {},
): Promise<void> {
  const entry = nextEntry({ from: complaint.status, to, mover, by, at: new Date(), ...extra });
  await updateDoc(doc(db, orgCollection(orgId, "complaints"), complaint.id), {
    status: to,
    timeline: [...complaint.timeline, entry],
    updatedAt: serverTimestamp(),
    ...(extra.assignee === undefined ? {} : { assignee: extra.assignee }),
  });
}

/**
 * A repair, raised by the resident who noticed it.
 *
 * Every field here is one the rules compare. A complaint has to name the unit
 * and the occupancy on the caller's own token, has to say who reported it, and
 * has to start at `reported`: the committee moves it on from there and a
 * resident who could set its status could close their own complaint.
 *
 * The rest is what the phone writes, field for field. This is the one
 * collection both clients write and neither function touches, so no compiler
 * or callable keeps the two in step; the shape comes from core instead. A
 * field named differently here reaches the phone with no description, no date
 * or no timeline.
 */
export async function raiseComplaint(
  orgId: string,
  fields: {
    unitId: string;
    buildingId: string;
    termId: string;
    description: string;
    category: ComplaintCategory;
    reporterUid: string;
  },
): Promise<void> {
  await addDoc(collection(db, orgCollection(orgId, "complaints")), {
    ...fields,
    orgId,
    status: "reported",
    assignee: null,
    timeline: [{ at: new Date().toISOString(), status: "reported", by: fields.reporterUid }],
    reportedAt: serverTimestamp(),
  });
}

/** A notice, posted by the committee. */
export async function postNotice(
  orgId: string,
  fields: {
    title: string;
    body: string;
    urgent: boolean;
    days: number | null;
    postedBy: string;
    buildingId: string | null;
  },
): Promise<void> {
  // The same shape the phone writes and the rules check.
  const notice = announcementInput(fields);
  await addDoc(collection(db, orgCollection(orgId, "announcements")), {
    orgId,
    buildingId: fields.buildingId,
    ...notice,
    postedBy: fields.postedBy,
    postedAt: serverTimestamp(),
  });
}

export async function removeNotice(orgId: string, id: string): Promise<void> {
  await deleteDoc(doc(db, orgCollection(orgId, "announcements"), id));
}

/* ---------------------------------------------------------------- chat -- */

/*
 * The building's chat, the same room the phone has.
 *
 * Opened only while somebody has the chat on screen, by `followChat` in the
 * store: every listener here is a read for every message sent, and the room is
 * meant to run inside Firestore's free allowance. Everything written is the
 * shape the rules check, and the rules are what keep order; what the page says
 * first is a courtesy.
 *
 * Nothing here removes anybody. A resident whose chat is off still lives in the
 * building and keeps reading, so the committee turns their chat off and on.
 */

export type { ChatMember, ChatMessage, ChatOffSpan, ChatSettings } from "@liyu/core";

function chatPath(orgId: string): string {
  return orgCollection(orgId, "chat");
}

function memberDoc(orgId: string, uid: string) {
  return doc(db, orgCollection(orgId, "chatMembers"), uid);
}

function roomDoc(orgId: string) {
  return doc(db, orgCollection(orgId, "chatSettings"), CHAT_ROOM);
}

/**
 * The newest messages, oldest first: the room's last few dozen, or the
 * smaller window the unread count reads. Estimated stamps, so a message just
 * sent sorts as sent.
 */
export function chatMessages(
  orgId: string,
  on: (rows: ChatMessage[]) => void,
  bad: (e: Error) => void,
  size: number = CHAT_WINDOW,
): Stop {
  return live(
    chatPath(orgId),
    (row) => toChatMessage(row.id, row.data({ serverTimestamps: "estimate" })),
    (rows) => on(inOrder(rows)),
    bad,
    orderBy("sentAt", "desc"),
    limit(size),
  );
}

/** What the committee pinned, newest first, however old. */
export function pinnedChat(
  orgId: string,
  on: (rows: ChatMessage[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    chatPath(orgId),
    (row) => toChatMessage(row.id, row.data({ serverTimestamps: "estimate" })),
    (rows) => on(inOrder(rows).reverse()),
    bad,
    where("pinned", "==", true),
  );
}

/** The reader's own membership: null until they have joined. */
export function chatMember(
  orgId: string,
  uid: string,
  on: (member: ChatMember | null) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(
    memberDoc(orgId, uid),
    (snapshot) => {
      const found = snapshot.data({ serverTimestamps: "estimate" });
      on(found ? toChatMember(uid, found) : null);
    },
    bad,
  );
}

/** Everybody who has opened the room, for the committee. */
export function chatMembers(
  orgId: string,
  on: (rows: ChatMember[]) => void,
  bad: (e: Error) => void,
): Stop {
  return live(
    orgCollection(orgId, "chatMembers"),
    (row) => toChatMember(row.id, row.data({ serverTimestamps: "estimate" })),
    on,
    bad,
  );
}

export function chatRoom(
  orgId: string,
  on: (settings: ChatSettings) => void,
  bad: (e: Error) => void,
): Stop {
  return onSnapshot(roomDoc(orgId), (snapshot) => on(toChatSettings(snapshot.data())), bad);
}

/**
 * Joining: your own membership, written the first time the room opens, so
 * there is a lastSentAt for the cooldown to be measured from.
 */
export async function joinChat(
  orgId: string,
  uid: string,
  role: ChatRole,
  unitId: string | null,
): Promise<void> {
  await setDoc(memberDoc(orgId, uid), {
    appRole: role,
    unitId: role === "committee" ? null : unitId,
    displayName: "",
    muted: false,
    lastSentAt: null,
    lastMessageId: null,
  });
}

/** A message and its author's lastSentAt, in one batch, which is what the cooldown is measured from. */
export async function sendChat(orgId: string, me: ChatMember, body: string): Promise<void> {
  const text = chatMessageInput(body);
  const message = doc(collection(db, chatPath(orgId)));
  const batch = writeBatch(db);
  batch.set(message, {
    authorUid: me.uid,
    appRole: me.role,
    unitId: me.unitId,
    displayName: me.displayName,
    body: text,
    sentAt: serverTimestamp(),
    pinned: false,
  });
  batch.update(memberDoc(orgId, me.uid), {
    lastSentAt: serverTimestamp(),
    lastMessageId: message.id,
  });
  await batch.commit();
}

export async function renameInChat(orgId: string, uid: string, name: string): Promise<void> {
  await updateDoc(memberDoc(orgId, uid), { displayName: chatNameInput(name) });
}

export async function pinChat(orgId: string, messageId: string, pinned: boolean): Promise<void> {
  await updateDoc(doc(db, chatPath(orgId), messageId), { pinned });
}

export async function removeChat(orgId: string, messageId: string): Promise<void> {
  await deleteDoc(doc(db, chatPath(orgId), messageId));
}

export async function turnChatOff(orgId: string, uid: string, span: ChatOffSpan): Promise<void> {
  await updateDoc(memberDoc(orgId, uid), { muted: true, mutedUntil: chatOffUntil(span) });
}

export async function turnChatOn(orgId: string, uid: string): Promise<void> {
  await updateDoc(memberDoc(orgId, uid), { muted: false, mutedUntil: null });
}

/** The committee putting somebody's name back to their door. */
export async function clearChatName(orgId: string, uid: string): Promise<void> {
  await updateDoc(memberDoc(orgId, uid), { displayName: "" });
}

/** The room's switches, written whole, the way the rules check them. */
export async function saveChatRoom(
  orgId: string,
  uid: string,
  settings: ChatSettings,
): Promise<void> {
  await setDoc(roomDoc(orgId), {
    cooldownSeconds: chatCooldownInput(settings.cooldownSeconds),
    pinnedCampaigns: settings.pinnedCampaigns,
    locked: settings.locked,
    updatedBy: uid,
    updatedAt: serverTimestamp(),
  });
}
